import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { toast } from 'sonner';
import ConfidenceSignals from '../components/ConfidenceSignals';
import TriggerSelector from '../components/TriggerSelector';
import RunStepBreakdown from '../components/RunStepBreakdown';
import {
  ArrowLeft, Lightning, Play, Pause, CheckCircle, XCircle, Clock,
  Pulse, EnvelopeSimple, WebhooksLogo, Timer, HandTap, ArrowsClockwise,
  CaretDown, CaretUp, PencilSimple, ChatCircle, Check, WarningCircle,
  UsersThree, Microphone, FileText, Globe, ChatText, TextAlignLeft,
} from '@phosphor-icons/react';

const TRIGGER_ICON = {
  email: EnvelopeSimple,
  webhook: WebhooksLogo,
  schedule: Timer,
  manual: HandTap,
  sms: ChatCircle,
};

const TRIGGER_LABEL = {
  email: 'A new enquiry is received',
  webhook: 'Someone submits a form',
  schedule: 'On a schedule',
  manual: 'Manually',
  sms: 'A message is received',
};

const WORKER_TYPE_ICON = {
  event: Lightning,
  chat: ChatText,
  voice: Microphone,
  file: FileText,
  operator: Globe,
  scheduled: Clock,
};

const STATUS_STYLE = {
  completed: { icon: CheckCircle, color: 'text-[#00E5C0]', bg: 'bg-[rgba(0,229,192,0.08)]', label: 'Completed' },
  running: { icon: Pulse, color: 'text-[#3B82F6]', bg: 'bg-[rgba(59,130,246,0.08)]', label: 'Running' },
  failed: { icon: XCircle, color: 'text-[#EF4444]', bg: 'bg-[rgba(239,68,68,0.08)]', label: 'Failed' },
  partially_completed: { icon: WarningCircle, color: 'text-[#F59E0B]', bg: 'bg-[rgba(245,158,11,0.08)]', label: 'Partially completed' },
  waiting: { icon: Clock, color: 'text-[#F59E0B]', bg: 'bg-[rgba(245,158,11,0.08)]', label: 'Waiting for approval' },
  queued: { icon: Clock, color: 'text-[#F59E0B]', bg: 'bg-[rgba(245,158,11,0.08)]', label: 'Queued' },
  cancelled: { icon: XCircle, color: 'text-[#7D8896]', bg: 'bg-[rgba(107,118,133,0.08)]', label: 'Cancelled' },
};

function timeAgo(iso) {
  if (!iso) return '';
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

const SOURCE_LABEL = {
  email: 'Email',
  webhook: 'Webhook',
  schedule: 'Schedule',
  sms: 'SMS',
  manual: 'Manual',
};

function RunRow({ run, onRetry, onApprovalAction }) {
  const isWaiting = run.status === 'waiting';
  const [expanded, setExpanded] = useState(isWaiting);
  const s = STATUS_STYLE[run.status] || STATUS_STYLE.queued;
  const Icon = s.icon;
  const isTriggered = run.shared_context?.triggered_automatically;
  const triggerType = run.trigger_type || 'manual';
  const sourceLabel = isTriggered ? `Triggered by ${SOURCE_LABEL[triggerType] || triggerType}` : 'Run manually';
  const canRetry = run.status === 'failed' || run.status === 'completed' || run.status === 'partially_completed';
  const failureReason = run.error || run.shared_context?.error;
  const isRetry = run.shared_context?.retry_of;

  return (
    <div
      className="rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.08)] transition-colors overflow-hidden"
      data-testid={`run-${run.system_run_id}`}
    >
      {/* Run header — clickable to expand */}
      <div
        className="flex items-center justify-between py-3 px-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
        data-testid={`run-header-${run.system_run_id}`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-6 h-6 rounded-full ${s.bg} flex items-center justify-center`}>
            <Icon size={12} className={s.color} weight="bold" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[13px] text-[#C8D1DB]">{s.label}</span>
              {isRetry && <span className="text-[9px] text-[#7D8896] bg-[rgba(255,255,255,0.04)] px-1.5 py-0.5 rounded">Retry</span>}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[10px] text-[#5C6778]">{sourceLabel}</span>
              <span className="text-[10px] text-[#2A2F38]">&middot;</span>
              <span className="text-[10px] text-[#3A4150]">{timeAgo(run.created_at)}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canRetry && onRetry && (
            <button
              onClick={(e) => { e.stopPropagation(); onRetry(run.system_run_id); }}
              data-testid={`retry-run-${run.system_run_id}`}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] text-[#7D8896] hover:text-[#B0B8C4] hover:bg-[rgba(255,255,255,0.04)] transition-colors border border-[rgba(255,255,255,0.08)]"
            >
              <ArrowsClockwise size={10} /> Retry
            </button>
          )}
          <CaretDown size={12} className={`text-[#3A4150] transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Failure reason (always visible if failed) */}
      {run.status === 'failed' && failureReason && !expanded && (
        <div className="px-4 pb-3 ml-9">
          <div className="px-3 py-1.5 rounded-lg bg-[rgba(239,68,68,0.04)] border border-[rgba(239,68,68,0.08)]" data-testid={`fail-reason-${run.system_run_id}`}>
            <span className="text-[10px] text-[#EF4444]">{typeof failureReason === 'string' ? failureReason.slice(0, 150) : 'Execution failed'}</span>
          </div>
        </div>
      )}

      {/* Expanded step breakdown */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-[rgba(255,255,255,0.08)]">
          <div className="pt-3">
            <RunStepBreakdown runId={run.system_run_id} onRetry={onRetry} onApprovalAction={onApprovalAction} />
          </div>
        </div>
      )}
    </div>
  );
}

export default function SystemDetailPage() {
  const { systemId } = useParams();
  const navigate = useNavigate();
  const [system, setSystem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [running, setRunning] = useState(false);
  const [showRuns, setShowRuns] = useState(true);
  const [triggerStatus, setTriggerStatus] = useState(null);
  const [editingTrigger, setEditingTrigger] = useState(false);
  const [editTriggerType, setEditTriggerType] = useState('');
  const [editTriggerConfig, setEditTriggerConfig] = useState({});
  const [savingTrigger, setSavingTrigger] = useState(false);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [showWebhookInfo, setShowWebhookInfo] = useState(false);

  const fetchSystem = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/systems/${systemId}`, { credentials: 'include' });
      if (!res.ok) { toast.error('Orchestration not found'); navigate('/systems'); return; }
      setSystem(await res.json());
    } catch { toast.error('Failed to load orchestration'); }
    finally { setLoading(false); }
  }, [systemId, navigate]);

  const fetchTriggerStatus = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/systems/${systemId}/trigger-status`, { credentials: 'include' });
      if (res.ok) setTriggerStatus(await res.json());
    } catch {}
  }, [systemId]);

  useEffect(() => { fetchSystem(); fetchTriggerStatus(); }, [fetchSystem, fetchTriggerStatus]);

  const handleToggle = async () => {
    setToggling(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/systems/${systemId}/toggle`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.status === 'active' ? 'Orchestration activated' : 'Orchestration paused');
        fetchSystem();
      }
    } catch { toast.error('Failed to toggle orchestration'); }
    finally { setToggling(false); }
  };

  const handleRun = async () => {
    setRunning(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/systems/${systemId}/run`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        toast.success('Orchestration run started');
        setTimeout(fetchSystem, 1500);
      } else {
        toast.error('Failed to start run');
      }
    } catch { toast.error('Failed to start run'); }
    finally { setRunning(false); }
  };

  const openTriggerEdit = () => {
    setEditTriggerType(system?.trigger_type || 'manual');
    setEditTriggerConfig(system?.trigger_config || {});
    setEditingTrigger(true);
  };

  const saveTrigger = async () => {
    setSavingTrigger(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/systems/${systemId}/trigger`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trigger_type: editTriggerType, trigger_config: editTriggerConfig }),
      });
      if (res.ok) {
        toast.success('Trigger updated');
        setEditingTrigger(false);
        fetchSystem();
        fetchTriggerStatus();
      } else {
        toast.error('Failed to update trigger');
      }
    } catch { toast.error('Failed to update trigger'); }
    finally { setSavingTrigger(false); }
  };

  const handleRetry = async (runId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/system-runs/${runId}/retry`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        toast.success('Retry started');
        setTimeout(fetchSystem, 1500);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Cannot retry this run');
      }
    } catch {
      toast.error('Failed to retry');
    }
  };

  const fetchWebhookInfo = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/systems/${systemId}/webhook-info`, { credentials: 'include' });
      if (res.ok) {
        setWebhookInfo(await res.json());
        setShowWebhookInfo(true);
      }
    } catch {}
  };

  if (loading) {
    return (
      <div className="max-w-[720px] mx-auto flex items-center justify-center py-32" data-testid="system-detail-loading">
        <ArrowsClockwise size={20} className="text-[#3A4150] animate-spin" />
      </div>
    );
  }

  if (!system) return null;

  const isActive = system.status === 'active';
  const TriggerIcon = TRIGGER_ICON[system.trigger_type] || HandTap;
  const recentRuns = system.recent_runs || [];

  return (
    <div className="max-w-[720px] mx-auto" data-testid="system-detail-page">
      {/* Back nav */}
      <button
        onClick={() => navigate('/systems')}
        data-testid="back-to-systems-btn"
        className="flex items-center gap-1 text-[#7D8896] hover:text-[#B0B8C4] text-xs transition-colors mb-6"
      >
        <ArrowLeft size={14} /> Systems
      </button>

      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-semibold text-[#F0F4F8]" data-testid="system-detail-name">{system.name}</h1>
            <div className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${isActive ? 'bg-[rgba(0,229,192,0.1)] text-[#00E5C0]' : 'bg-[rgba(107,118,133,0.1)] text-[#7D8896]'}`} data-testid="system-status-badge">
              {isActive ? 'Active' : 'Paused'}
            </div>
          </div>
          {system.description && (
            <p className="text-sm text-[#7D8896] mt-1">{system.description}</p>
          )}
          <div className="flex items-center gap-2 text-[11px] text-[#5C6778] mt-3">
            <TriggerIcon size={12} />
            <span>{TRIGGER_LABEL[system.trigger_type] || 'Manual trigger'}</span>
            {system.run_count > 0 && (
              <>
                <span className="text-[#2A2F38]">&middot;</span>
                <span>{system.run_count} run{system.run_count !== 1 ? 's' : ''}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleToggle}
            disabled={toggling}
            data-testid="toggle-system-btn"
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
              isActive
                ? 'border-[rgba(255,255,255,0.08)] text-[#7D8896] hover:text-[#B0B8C4] hover:border-[rgba(255,255,255,0.12)]'
                : 'border-[rgba(0,229,192,0.2)] text-[#00E5C0] hover:bg-[rgba(0,229,192,0.05)]'
            } disabled:opacity-50`}
          >
            {isActive ? <Pause size={12} /> : <Play size={12} />}
            {isActive ? 'Pause' : 'Activate'}
          </button>
          <button
            onClick={handleRun}
            disabled={running || !isActive}
            data-testid="run-system-btn"
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#00E5C0] text-[#12161C] text-xs font-semibold hover:bg-[#00CCB0] transition-colors disabled:opacity-40"
          >
            {running ? <ArrowsClockwise size={12} className="animate-spin" /> : <Lightning size={12} weight="fill" />}
            {running ? 'Starting...' : 'Run Now'}
          </button>
        </div>
      </div>

      {/* Confidence signals */}
      <div className="mb-6">
        <ConfidenceSignals systemId={systemId} />
      </div>

      {/* Runs when... trigger section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs font-semibold text-[rgba(255,255,255,0.75)] uppercase tracking-[0.08em] pb-2 border-b border-[rgba(255,255,255,0.08)]">Runs when...</h2>
          {!editingTrigger && (
            <button
              onClick={openTriggerEdit}
              data-testid="edit-trigger-btn"
              className="flex items-center gap-1 text-[11px] text-[#5C6778] hover:text-[#B0B8C4] transition-colors"
            >
              <PencilSimple size={11} /> Edit
            </button>
          )}
        </div>

        {editingTrigger ? (
          <div className="bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-2xl p-5" data-testid="trigger-edit-panel">
            <TriggerSelector
              selected={editTriggerType}
              config={editTriggerConfig}
              onSelect={setEditTriggerType}
              onConfigChange={setEditTriggerConfig}
              compact
            />
            <div className="flex items-center gap-3 mt-4 pt-4 border-t border-[rgba(255,255,255,0.08)]">
              <button
                onClick={() => setEditingTrigger(false)}
                className="px-3 py-2 rounded-lg text-xs text-[#7D8896] hover:text-[#B0B8C4] transition-colors"
                data-testid="cancel-trigger-btn"
              >
                Cancel
              </button>
              <button
                onClick={saveTrigger}
                disabled={savingTrigger}
                data-testid="save-trigger-btn"
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[rgba(0,229,192,0.1)] text-[#00E5C0] text-xs font-medium hover:bg-[rgba(0,229,192,0.15)] transition-colors border border-[rgba(0,229,192,0.2)] disabled:opacity-50"
              >
                {savingTrigger ? <ArrowsClockwise size={11} className="animate-spin" /> : <Check size={11} />}
                {savingTrigger ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-2xl p-5" data-testid="trigger-summary">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[rgba(0,229,192,0.08)] flex items-center justify-center flex-shrink-0">
                <TriggerIcon size={16} className="text-[#00E5C0]" />
              </div>
              <div className="flex-1">
                <span className="text-[13px] text-[#F0F4F8] font-medium block">
                  {TRIGGER_LABEL[system.trigger_type] || 'Manually'}
                </span>
                {system.trigger_type === 'schedule' && system.trigger_config?.frequency && (
                  <span className="text-[11px] text-[#5C6778]">
                    {system.trigger_config.frequency === 'hourly' ? 'Every hour' :
                     `${system.trigger_config.frequency.charAt(0).toUpperCase() + system.trigger_config.frequency.slice(1)}${system.trigger_config.time ? ` at ${system.trigger_config.time}` : ''}`}
                  </span>
                )}
                {system.trigger_type === 'webhook' && triggerStatus?.webhook_url && (
                  <span className="text-[10px] text-[#3A4150] block mt-1 font-mono truncate">
                    {triggerStatus.webhook_url}
                  </span>
                )}
              </div>

              {/* Webhook info toggle */}
              {system.trigger_type === 'webhook' && (
                <button
                  onClick={fetchWebhookInfo}
                  data-testid="view-webhook-info-btn"
                  className="text-[10px] text-[#5C6778] hover:text-[#B0B8C4] transition-colors mr-2"
                >
                  {showWebhookInfo ? 'Hide' : 'Details'}
                </button>
              )}
              {/* Status indicator */}
              {triggerStatus && (
                <div className="flex-shrink-0">
                  {triggerStatus.status === 'ready' ? (
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[rgba(0,229,192,0.06)]" data-testid="trigger-status-ready">
                      <CheckCircle size={11} className="text-[#00E5C0]" weight="bold" />
                      <span className="text-[10px] text-[#00E5C0] font-medium">Ready</span>
                    </div>
                  ) : triggerStatus.status === 'needs_connection' ? (
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[rgba(245,158,11,0.06)]" data-testid="trigger-status-blocked">
                      <WarningCircle size={11} className="text-[#F59E0B]" weight="bold" />
                      <span className="text-[10px] text-[#F59E0B] font-medium">{triggerStatus.message}</span>
                    </div>
                  ) : triggerStatus.status === 'needs_config' ? (
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[rgba(59,130,246,0.06)]" data-testid="trigger-status-config">
                      <PencilSimple size={11} className="text-[#3B82F6]" weight="bold" />
                      <span className="text-[10px] text-[#3B82F6] font-medium">{triggerStatus.message}</span>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Webhook info panel (expandable) */}
        {showWebhookInfo && webhookInfo && system.trigger_type === 'webhook' && (
          <div className="mt-3 bg-[#0E1218] border border-[rgba(255,255,255,0.05)] rounded-xl p-4" data-testid="webhook-info-panel">
            <div className="space-y-3">
              <div>
                <span className="text-[10px] text-[#5C6778] uppercase tracking-[0.08em] font-semibold block mb-1">Webhook URL</span>
                <div className="flex items-center gap-2">
                  <code className="text-[11px] text-[#B0B8C4] font-mono bg-[rgba(255,255,255,0.03)] px-2 py-1 rounded flex-1 truncate" data-testid="webhook-url-display">
                    {webhookInfo.webhook_url}
                  </code>
                  <button
                    onClick={() => { navigator.clipboard.writeText(webhookInfo.webhook_url); toast.success('URL copied'); }}
                    className="text-[10px] text-[#5C6778] hover:text-[#00E5C0] transition-colors"
                    data-testid="copy-webhook-url"
                  >
                    Copy
                  </button>
                </div>
              </div>
              <div>
                <span className="text-[10px] text-[#5C6778] uppercase tracking-[0.08em] font-semibold block mb-1">Signing secret</span>
                <div className="flex items-center gap-2">
                  <code className="text-[11px] text-[#B0B8C4] font-mono bg-[rgba(255,255,255,0.03)] px-2 py-1 rounded flex-1 truncate" data-testid="webhook-secret-display">
                    {webhookInfo.webhook_secret}
                  </code>
                  <button
                    onClick={() => { navigator.clipboard.writeText(webhookInfo.webhook_secret); toast.success('Secret copied'); }}
                    className="text-[10px] text-[#5C6778] hover:text-[#00E5C0] transition-colors"
                    data-testid="copy-webhook-secret"
                  >
                    Copy
                  </button>
                </div>
              </div>
              <p className="text-[10px] text-[#3A4150] leading-relaxed">
                {webhookInfo.signing_instructions}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* AI Team Org Chart — shown when system has worker_ids (multi-worker orchestration) */}
      {(system.worker_ids || []).length > 0 && (
        <div className="mb-8" data-testid="system-ai-team">
          <h2 className="text-xs font-semibold text-[rgba(255,255,255,0.75)] uppercase tracking-[0.08em] pb-2 border-b border-[rgba(255,255,255,0.08)] mb-4">
            AI Team
          </h2>
          <div className="bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-2xl p-6 shadow-[0_1px_3px_rgba(0,0,0,0.14),0_6px_20px_rgba(0,0,0,0.10)]">
            {/* Supervisor node */}
            <div className="flex justify-center" data-testid="supervisor-node">
              <div className="bg-[rgba(0,229,192,0.04)] border border-[rgba(0,229,192,0.15)] rounded-xl px-5 py-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[rgba(0,229,192,0.1)] flex items-center justify-center">
                  <Lightning size={18} className="text-[#00E5C0]" weight="fill" />
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-[#F0F4F8]">{system.name}</p>
                  <p className="text-[10px] text-[#00E5C0]">Supervisor</p>
                </div>
              </div>
            </div>

            {/* Vertical connector from supervisor */}
            <div className="flex justify-center">
              <div className="w-px h-6 bg-[rgba(0,229,192,0.2)]" />
            </div>

            {/* Horizontal branch rail */}
            {(() => {
              const workerSteps = (system.steps || []).filter(s => s.type === 'worker');
              const count = workerSteps.length;
              if (count === 0) return null;
              return (
                <>
                  {count > 1 && (
                    <div className="flex justify-center px-8">
                      <div className="h-px bg-[rgba(0,229,192,0.12)] w-full max-w-[600px]" />
                    </div>
                  )}

                  {/* Worker nodes */}
                  <div className="flex justify-center">
                    <div className={`flex flex-wrap justify-center ${count <= 3 ? 'gap-6' : 'gap-4'} mt-0`}>
                      {workerSteps.map((step) => {
                        const TypeIcon = WORKER_TYPE_ICON[step.worker_type] || Lightning;
                        const hasDetail = Boolean(step.worker_id);
                        return (
                          <div key={step.worker_id} className="flex flex-col items-center" data-testid={`team-worker-${step.worker_id}`}>
                            {/* Vertical drop from rail */}
                            <div className="w-px h-5 bg-[rgba(0,229,192,0.12)]" />
                            {/* Worker card */}
                            <button
                              type="button"
                              onClick={() => { if (hasDetail) navigate(`/workers/${step.worker_id}/detail`); }}
                              disabled={!hasDetail}
                              data-testid={`team-worker-card-${step.worker_id}`}
                              className={`bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)] rounded-xl p-4 w-44 text-left transition-all ${
                                hasDetail
                                  ? 'hover:border-[rgba(0,229,192,0.35)] hover:bg-[rgba(0,229,192,0.04)] cursor-pointer focus:outline-none focus:ring-1 focus:ring-[rgba(0,229,192,0.35)]'
                                  : 'cursor-default hover:border-[rgba(255,255,255,0.12)]'
                              }`}
                            >
                              <div className="flex items-center gap-2.5 mb-2">
                                <div className="w-7 h-7 rounded-lg bg-[rgba(0,229,192,0.08)] flex items-center justify-center flex-shrink-0">
                                  <TypeIcon size={14} className="text-[#00E5C0]" />
                                </div>
                                <div className="w-1.5 h-1.5 rounded-full bg-[#4ADE80] ml-auto flex-shrink-0" />
                              </div>
                              <p className="text-[12px] text-[#F0F4F8] font-medium truncate">{step.label}</p>
                              <p className="text-[10px] text-[#7D8896] capitalize mt-0.5">{step.worker_type || 'event'} worker</p>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Supervisor Instructions — shown when present */}
      {system.supervisor_instructions && (
        <div className="mb-8" data-testid="system-supervisor-instructions">
          <h2 className="text-xs font-semibold text-[rgba(255,255,255,0.75)] uppercase tracking-[0.08em] pb-2 border-b border-[rgba(255,255,255,0.08)] mb-4">
            Supervisor Instructions
          </h2>
          <div className="bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-2xl p-5 shadow-[0_1px_3px_rgba(0,0,0,0.14),0_6px_20px_rgba(0,0,0,0.10)]">
            <div className="flex items-start gap-3">
              <TextAlignLeft size={14} className="text-[#7D8896] mt-0.5 flex-shrink-0" />
              <p className="text-[13px] text-[#C8D1DB] whitespace-pre-wrap leading-relaxed" data-testid="supervisor-instructions-text">
                {system.supervisor_instructions}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Steps flow — legacy view for template-based systems without worker_ids */}
      {(system.worker_ids || []).length === 0 && (system.steps || []).length > 0 && (
      <div className="mb-8">
        <h2 className="text-xs font-semibold text-[rgba(255,255,255,0.75)] uppercase tracking-[0.08em] pb-2 border-b border-[rgba(255,255,255,0.08)] mb-4">What this orchestration does</h2>
        <div className="bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-2xl p-6 shadow-[0_1px_3px_rgba(0,0,0,0.14),0_6px_20px_rgba(0,0,0,0.10)]" data-testid="system-steps">
          {(system.steps || []).map((step, i) => (
            <div key={i}>
              <div className="flex items-center gap-4">
                <div className="w-7 h-7 rounded-full bg-[rgba(0,229,192,0.08)] flex items-center justify-center text-[10px] font-bold text-[#00E5C0] flex-shrink-0">
                  {i + 1}
                </div>
                <div className="flex-1">
                  <span className="text-[13px] text-[#F0F4F8] font-medium">{step.label}</span>
                  {step.group && (
                    <span className="text-[10px] text-[#3A4150] ml-2 capitalize">{step.group}</span>
                  )}
                </div>
                {step.approval_required && (
                  <span className="flex items-center gap-1 text-[9px] text-[#F59E0B] bg-[rgba(245,158,11,0.06)] px-2 py-0.5 rounded-full" data-testid={`step-approval-badge-${i}`}>
                    <WarningCircle size={9} weight="bold" /> Approval
                  </span>
                )}
                {step.type === 'ai' && (
                  <span className="text-[9px] text-[#7D8896] bg-[rgba(255,255,255,0.04)] px-2 py-0.5 rounded-full">AI</span>
                )}
              </div>
              {i < (system.steps || []).length - 1 && (
                <div className="flex ml-3.5">
                  <div className="w-px h-5 bg-[rgba(0,229,192,0.1)]" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Recent runs */}
      <div>
        <button
          onClick={() => setShowRuns(!showRuns)}
          className="flex items-center gap-2 text-xs font-semibold text-[rgba(255,255,255,0.75)] uppercase tracking-[0.08em] mb-4 hover:text-[#F0F4F8] transition-colors"
          data-testid="toggle-runs-section"
        >
          Recent runs
          {showRuns ? <CaretUp size={10} /> : <CaretDown size={10} />}
          {recentRuns.length > 0 && <span className="text-[#3A4150] font-normal normal-case">({recentRuns.length})</span>}
        </button>

        {showRuns && (
          <div className="space-y-2" data-testid="recent-runs-list">
            {recentRuns.length === 0 ? (
              <p className="text-sm text-[#5C6778] py-6 text-center">No runs yet. Click "Run Now" to start this orchestration.</p>
            ) : (
              recentRuns.map(run => <RunRow key={run.system_run_id} run={run} onRetry={handleRetry} onApprovalAction={() => { setTimeout(fetchSystem, 1500); }} />)
            )}
          </div>
        )}
      </div>
    </div>
  );
}
